import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartBubbleAssistiveText - The accessible cart count text.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartBubbleAssistiveText'];
  /** @type {number | null} */
  #reconcileTimer = null;
  /** @type {boolean} */
  #isReconciling = false;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.#reconcileTimer) {
      clearTimeout(this.#reconcileTimer);
      this.#reconcileTimer = null;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const eventData = event.detail?.data || {};
    const rawItemCount = eventData.itemCount;
    const rawItemDelta = eventData.itemDelta;
    const hasNumericCount = Number.isFinite(Number(rawItemCount));
    const hasNumericDelta = Number.isFinite(Number(rawItemDelta));

    // Some cart:update emitters don't include itemCount/itemDelta, so avoid coercing to 0.
    // Reconcile against /cart.js instead of hiding/resetting the badge.
    if (!hasNumericCount && !hasNumericDelta) {
      this.#scheduleReconcileFromServer();
      return;
    }

    const previousCount = this.currentCartCount;
    const nextCount = hasNumericDelta
      ? Math.max(0, previousCount + Number(rawItemDelta))
      : Math.max(0, Number(rawItemCount));
    const shouldAnimate = nextCount > previousCount;

    this.renderCartBubble(nextCount, shouldAnimate);
    this.#scheduleReconcileFromServer();
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} animate - Whether to animate the cart bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const normalizedItemCount = Number.isFinite(itemCount) ? Math.max(0, Number(itemCount)) : 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', normalizedItemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', normalizedItemCount === 0);

    this.currentCartCount = normalizedItemCount;
    const cartCountLabel = this.refs.cartBubbleAssistiveText.dataset.cartCountLabel || 'Items in cart';
    this.refs.cartBubbleAssistiveText.textContent = `${cartCountLabel}: ${normalizedItemCount}`;

    this.classList.toggle('header-actions__cart-icon--has-cart', normalizedItemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || normalizedItemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Reconciles cart bubble count with server truth.
   * Guards against event payloads that provide deltas or omit itemCount.
   */
  #scheduleReconcileFromServer = () => {
    if (this.#reconcileTimer) {
      clearTimeout(this.#reconcileTimer);
    }

    this.#reconcileTimer = window.setTimeout(async () => {
      if (this.#isReconciling) return;
      this.#isReconciling = true;

      try {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const cart = await response.json();
        const serverCount = Number(cart?.item_count || 0);
        this.renderCartBubble(serverCount, false);
      } catch (_) {
        // no-op; optimistic count remains
      } finally {
        this.#isReconciling = false;
      }
    }, 150);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
